/**
 * useServerListControls — wspólny stan sortu serwerowego + paginacji dla list osadzonych na
 * silniku list (`@/lib/list-engine` po stronie API). Zastępuje kopiowany per-strona wzorzec
 * (osobne `SORTABLE_COLUMN_ID_TO_SLUG` + ręczny stan page/sort) jedną, sterowaną metadanymi
 * implementacją: sortowalność kolumn SYSTEMOWYCH front bierze z `meta.sortable` odpowiedzi
 * (autorytatywne, backend wyprowadza je automatem z projekcji), więc żadna strona nie utrzymuje
 * już własnej listy sortowalnych kolumn — koniec dryfu slug↔kolumna.
 *
 * Kontrakt drutowy silnika: `?sort=<slug>&order=asc|desc&limit=<n>&offset=<page*pageSize>`
 * (strona 0-indeksowana). Slug kolumny wyprowadzamy z mapy `columnIdToSortSlug` (ta sama mapa
 * co dla `?columns=` — slugi sortowalne są podzbiorem slugów projekcji), z fallbackiem do
 * `columnId` (kolumny, których id == slug — w tym pola dodatkowe/ext, gdzie id = slug pola).
 *
 * Pola dodatkowe (ext): backend honoruje `?sort=<ext_slug>` niezależnie od `meta.sortable`
 * (leniwie rozwiązuje katalog), a sortowalność kolumny ext front zna z jej definicji (typ pola —
 * `buildExtColumns` ustawia `sortable`), więc `isCoreColumnSortable` dotyczy WYŁĄCZNIE kolumn
 * systemowych; kolumn ext nie sprawdzaj przez `meta.sortable`.
 */

import { useCallback, useMemo, useState } from 'react';
import { defaultPageConfig, MAX_LIST_LIMIT } from '@saas/shared';

import type { CalendarRange, ColumnHeaderFilter } from './types';

// Typ zakresu kalendarza żyje w `./types` (jedna definicja dla propa tabeli, kalendarza i hooka).
export type { CalendarRange };

/**
 * Rozmiar zapytania listy w trybie kalendarza: siatka nie stronicuje (nawigacja idzie miesiącami,
 * stopka pokazuje sam licznik), więc pobieramy od początku tyle rekordów miesiąca, ile serwer
 * pozwala naraz — górna granica `?limit=` silnika list. To TA SAMA stała co po stronie API
 * (`MAX_LIST_LIMIT` z `@saas/shared`, do której przycina `parseListPagination`), więc zmiana
 * limitu serwera nie rozjedzie się z kalendarzem. Gdy miesiąc ma więcej wpisów, `CalendarBoard`
 * mówi o tym komunikatem zamiast rysować po cichu część.
 */
export const CALENDAR_FETCH_LIMIT = MAX_LIST_LIMIT;

/** Metadana listy z silnika — slugi sortowalnych / filtrowalnych kolumn systemowych. */
export interface ListResponseMeta {
  sortable: string[];
  /**
   * Slugi filtrowalnych kolumn systemowych (`meta.filterable`) — analogicznie do `sortable`
   * (backend wyprowadza je automatem z projekcji). Opcjonalne: trasy jeszcze niezmigrowane na
   * filtr z silnika tej metadanej nie zwracają (front spada wtedy na flagę katalogu `isFilterable`).
   */
  filterable?: string[];
}

export interface ServerListControlsOptions {
  /**
   * Startowy rozmiar strony. Domyślnie = domyślne „Rekordów na stronę" z Kreatora Listy
   * (`defaultPageConfig.pageSize`), żeby lista bez zapisanego ustawienia startowała od tej samej
   * wartości, którą podpowiada kreator. Zapisane ustawienie widoku stosuje potem
   * `ConfigurableTable`: gdy widok nie ma ustawienia albo ma 25, lista pobiera dane raz; przy innej
   * zapisanej wartości drugie zapytanie leci po wczytaniu widoku (jak dawniej przy 50).
   */
  defaultPageSize?: number;
  /** Domyślna kolumna sortu (column id). Brak = sort domyślny serwera (`defaultOrderBy`). */
  defaultSortColumnId?: string;
  /** Domyślny kierunek sortu (default 'desc'). */
  defaultSortDirection?: 'asc' | 'desc';
  /**
   * Mapa `id kolumny → slug backendu` dla `?sort=` (ta sama mapa co dla `?columns=`). Dla kolumn,
   * których id == slug (w tym pola ext), wpis można pominąć — fallback do `columnId`.
   */
  columnIdToSortSlug?: Readonly<Record<string, string>>;
}

export interface ServerListControls {
  page: number;
  pageSize: number;
  sortColumnId: string | undefined;
  sortDirection: 'asc' | 'desc';
  /** Propsy sortu/paginacji do rozlania na `<ConfigurableTable/>` (razem z `totalItems`). */
  tableProps: {
    page: number;
    pageSize: number;
    sortColumn: string | undefined;
    sortDirection: 'asc' | 'desc';
    onSort: (column: string, direction: 'asc' | 'desc') => void;
    onPageChange: (page: number) => void;
    onPageSizeChange: (size: number) => void;
  };
  /**
   * Parametry zapytania listy do dorzucenia do `URLSearchParams` (`sort` pominięty gdy brak). W
   * trybie kalendarza (`calendarRange` ustawiony) `limit` = `CALENDAR_FETCH_LIMIT`, `offset` = 0 —
   * niezależnie od rozmiaru strony tabeli.
   */
  queryParams: { sort?: string; order: 'asc' | 'desc'; limit: number; offset: number };
  /** Reset do pierwszej strony — wołaj na zmianę filtra / wyszukiwarki / zakresu firm. */
  resetPage: () => void;
  /** Zakres dat widocznego miesiąca kalendarza (null poza trybem kalendarza). */
  calendarRange: CalendarRange | null;
  /** Ustawia zakres miesiąca kalendarza i wraca na pierwszą stronę (podpinane pod `onCalendarRangeChange`). */
  setCalendarRange: (range: CalendarRange | null) => void;
  /** Gotowy wpis filtra `mode: 'range'` z zakresu kalendarza (null gdy brak) — strona dokłada go do `headerFilters`. */
  calendarHeaderFilter: ColumnHeaderFilter | null;
  /** Czy kolumna SYSTEMOWA jest sortowalna serwerowo wg `meta.sortable` (NIE dla kolumn ext). */
  isCoreColumnSortable: (columnId: string, sortableSlugs: string[] | undefined) => boolean;
  /** Czy kolumna SYSTEMOWA jest filtrowalna serwerowo wg `meta.filterable` (NIE dla kolumn ext). */
  isCoreColumnFilterable: (columnId: string, filterableSlugs: string[] | undefined) => boolean;
}

export function useServerListControls(options: ServerListControlsOptions = {}): ServerListControls {
  const {
    defaultPageSize = defaultPageConfig.pageSize,
    defaultSortColumnId,
    defaultSortDirection = 'desc',
    columnIdToSortSlug,
  } = options;

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [sortColumnId, setSortColumnId] = useState<string | undefined>(defaultSortColumnId);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(defaultSortDirection);
  const [calendarRange, setCalendarRangeState] = useState<CalendarRange | null>(null);

  // Zmiana sortu/rozmiaru strony wraca na pierwszą stronę (paginacja OFFSET — inaczej można
  // wylądować na nieistniejącej stronie albo zobaczyć niespójny wycinek).
  const onSort = useCallback((column: string, direction: 'asc' | 'desc') => {
    setSortColumnId(column);
    setSortDirection(direction);
    setPage(0);
  }, []);
  const onPageChange = useCallback((next: number) => setPage(Math.max(next, 0)), []);
  const onPageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setPage(0);
  }, []);
  const resetPage = useCallback(() => setPage(0), []);

  // Zmiana miesiąca kalendarza wraca na pierwszą stronę (jak każdy filtr). Porównanie po wartości —
  // `CalendarBoard` emituje przy montażu i przy każdej nawigacji miesiąca, więc chronimy przed
  // zbędnym setState/refetchem, gdy zakres faktycznie się nie zmienił.
  const setCalendarRange = useCallback((range: CalendarRange | null) => {
    setCalendarRangeState((prev) => {
      if (prev === range) return prev;
      if (
        prev &&
        range &&
        prev.start === range.start &&
        prev.end === range.end &&
        prev.fieldSlug === range.fieldSlug &&
        prev.endFieldSlug === range.endFieldSlug
      ) {
        return prev;
      }
      return range;
    });
    setPage(0);
  }, []);

  const calendarHeaderFilter = useMemo<ColumnHeaderFilter | null>(
    () =>
      calendarRange
        ? {
            field: calendarRange.fieldSlug,
            mode: 'range',
            rangeMin: calendarRange.start,
            rangeMax: calendarRange.end,
            // Pole końca → serwer bierze rekordy, których przedział nachodzi na miesiąc.
            ...(calendarRange.endFieldSlug ? { endField: calendarRange.endFieldSlug } : {}),
          }
        : null,
    [calendarRange],
  );

  const resolveSlug = useCallback(
    (columnId: string): string => columnIdToSortSlug?.[columnId] ?? columnId,
    [columnIdToSortSlug],
  );

  // Tryb kalendarza: jedno zapytanie o cały miesiąc (do limitu serwera) od początku — tabela w tym
  // trybie nie stronicuje, więc rozmiar i numer strony tabeli nie mają tu znaczenia.
  const inCalendar = calendarRange !== null;
  const queryParams = useMemo(
    () => ({
      sort: sortColumnId ? resolveSlug(sortColumnId) : undefined,
      order: sortDirection,
      limit: inCalendar ? CALENDAR_FETCH_LIMIT : pageSize,
      offset: inCalendar ? 0 : page * pageSize,
    }),
    [sortColumnId, sortDirection, pageSize, page, resolveSlug, inCalendar],
  );

  const isCoreColumnSortable = useCallback(
    (columnId: string, sortableSlugs: string[] | undefined): boolean =>
      Array.isArray(sortableSlugs) && sortableSlugs.includes(resolveSlug(columnId)),
    [resolveSlug],
  );

  // Filtr używa sluga kolumny WPROST (nie remapu sortu `columnIdToSortSlug`): `meta.filterable`
  // niesie slugi backendu = slugi kolumn generycznych. Kolumny ze specjalnym widżetem (firma/
  // dostawca) mają filtr zaszyty w nadpisaniu, nie przez ten predykat.
  const isCoreColumnFilterable = useCallback(
    (columnId: string, filterableSlugs: string[] | undefined): boolean =>
      Array.isArray(filterableSlugs) && filterableSlugs.includes(columnId),
    [],
  );

  const tableProps = useMemo(
    () => ({
      page,
      pageSize,
      sortColumn: sortColumnId,
      sortDirection,
      onSort,
      onPageChange,
      onPageSizeChange,
    }),
    [page, pageSize, sortColumnId, sortDirection, onSort, onPageChange, onPageSizeChange],
  );

  return {
    page,
    pageSize,
    sortColumnId,
    sortDirection,
    tableProps,
    queryParams,
    resetPage,
    calendarRange,
    setCalendarRange,
    calendarHeaderFilter,
    isCoreColumnSortable,
    isCoreColumnFilterable,
  };
}
